import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { queryKeys } from '@/constants/queryKeys'
import { getFeatures } from '@/domains/Business/api'
import { Feature, Features, Permission, Role } from '@/domains/Business/types'

import { useBusinessRole } from './useBusinessRole'
import { useBusinessUser } from './useBusinessUser'

type FeatureFlags = {
  [K in Feature]: boolean
}

export function useFeatures(features: Feature[]) {
  const { role, isPending: rolePending } = useBusinessRole()
  const businessUser = useBusinessUser()

  const featuresQuery = useQuery({
    queryKey: [queryKeys.getFeatures],
    queryFn: getFeatures,
    select: (data: AxiosResponse<Features>) => data.data,
  })

  const hasContractorsEnabled = useMemo(() => {
    return featuresQuery.data?.features.includes(Feature.CONTRACTORS) ?? false
  }, [featuresQuery.data])

  const hasCardsEnabled = useMemo(() => {
    return featuresQuery.data?.features.includes(Feature.CARDS) ?? false
  }, [featuresQuery.data])

  const hasEURCWalletEnabled = useMemo(() => {
    return featuresQuery.data?.features.includes(Feature.EURC_WALLET) ?? false
  }, [featuresQuery.data])

  const enabledFeatures = useMemo((): FeatureFlags => {
    if (!role) {
      return {
        [Feature.ACCOUNTS]: true,
        [Feature.ACTIONS]: true,
        [Feature.CARDS]: true,
        [Feature.HOME]: true,
        [Feature.RECIPIENTS]: true,
        [Feature.SETTINGS]: false,
        [Feature.TASKS]: true,
        [Feature.TEAM]: true,
        [Feature.TRANSACTIONS]: true,
        [Feature.CONTRACTORS]: false,
        [Feature.EURC_WALLET]: false,
      }
    }

    const BASE_ACCESS_ROLES = [Role.ADMIN, Role.READ_ONLY, Role.PAYMENT_OPS]

    return {
      [Feature.HOME]: BASE_ACCESS_ROLES.includes(role),
      [Feature.ACCOUNTS]: BASE_ACCESS_ROLES.includes(role),
      [Feature.CARDS]:
        [...BASE_ACCESS_ROLES, Role.CARD_USER].includes(role) &&
        hasCardsEnabled,
      [Feature.RECIPIENTS]: BASE_ACCESS_ROLES.includes(role),
      [Feature.TRANSACTIONS]: BASE_ACCESS_ROLES.includes(role),
      [Feature.TEAM]: [Role.ADMIN].includes(role),
      [Feature.TASKS]: [Role.ADMIN, Role.PAYMENT_OPS].includes(role),
      [Feature.ACTIONS]: !!businessUser?.permissions.includes(
        Permission.TRANSFERS,
      ),
      [Feature.CONTRACTORS]:
        [Role.ADMIN].includes(role) && hasContractorsEnabled,

      [Feature.SETTINGS]: false,
      [Feature.EURC_WALLET]: hasEURCWalletEnabled,
    }
  }, [
    businessUser?.permissions,
    hasContractorsEnabled,
    hasCardsEnabled,
    hasEURCWalletEnabled,
    role,
  ])

  return {
    features: features.map((feature) => enabledFeatures[feature]),
    isPending: featuresQuery.isPending || rolePending,
    featuresAvailable: !!featuresQuery.data?.features,
  }
}

export function useFeature(feature: Feature) {
  const features = useFeatures([feature])

  const [isEnabled] = features.features

  return {
    isEnabled,
    isPending: features.isPending,
  }
}
